import React from 'react'

export type ComparisonTone = 'positive' | 'caution' | 'negative' | 'neutral'
export type ComparisonPreference = 'higher_better' | 'lower_better' | 'neutral'

type ComparisonPresentationArgs = {
  value: unknown
  rowLabel?: string
  rowValues?: unknown[]
  explicitColor?: string | null
}

const LOWER_BETTER_KEYWORDS = [
  'cost', 'price', 'expense', 'burn', 'latency', 'risk', 'probability', 'likelihood',
  'volatility', 'churn', 'downtime', 'delay', 'debt', 'defect', 'failure', 'loss',
  'emission', 'exposure', 'complexity', 'capex', 'payback', 'cac', 'barrier', 'tax',
  'fees', 'time to', 'timeline', 'recession', 'drawdown', 'threat', 'inflation',
  'unemployment', 'deficit', 'default', 'corruption',
]

const HIGHER_BETTER_KEYWORDS = [
  'growth', 'revenue', 'profit', 'margin', 'roi', 'return', 'coverage', 'adoption',
  'accuracy', 'confidence', 'strength', 'quality', 'capacity', 'yield', 'resilience',
  'readiness', 'retention', 'market share', 'demand', 'score', 'fit', 'compliance',
  'advantage', 'opportunity', 'throughput', 'productivity', 'efficiency', 'gdp',
  'gdp per capita', 'income', 'exports', 'export', 'fdi', 'inflow', 'stability',
  'reserves', 'attendance', 'employment',
]

const POSITIVE_TERMS = [
  'leader', 'leading', 'best', 'strong', 'excellent', 'favorable', 'attractive',
  'supported', 'confirmed', 'compliant', 'pass', 'advantage', 'bullish', 'high upside',
]

const CAUTION_TERMS = [
  'medium', 'moderate', 'partial', 'mixed', 'balanced', 'watch', 'emerging',
  'uncertain', 'trade-off', 'conditional',
]

const NEGATIVE_TERMS = [
  'laggard', 'weak', 'poor', 'unfavorable', 'rejected', 'refuted', 'non compliant',
  'non-compliant', 'bearish', 'risk', 'concern', 'challenge', 'expensive',
]

export function getComparisonDisplayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'string') return value
  if (typeof value === 'number') return Number.isFinite(value) ? value.toLocaleString() : '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'

  if (typeof value === 'object') {
    const candidate = value as Record<string, unknown>
    if (typeof candidate.formatted === 'string' && candidate.formatted.trim()) return candidate.formatted
    if (candidate.value !== undefined && candidate.value !== null) return getComparisonDisplayValue(candidate.value)
    if (typeof candidate.text === 'string' && candidate.text.trim()) return candidate.text
    if (typeof candidate.label === 'string' && candidate.label.trim()) return candidate.label
  }

  return String(value)
}

function normalizeText(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[_/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function parseComparableNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const display = getComparisonDisplayValue(value)
  const match = display.match(/-?\d[\d,]*\.?\d*/)
  if (!match) return null
  const parsed = Number.parseFloat(match[0].replace(/,/g, ''))
  return Number.isFinite(parsed) ? parsed : null
}

function mapExplicitColorToTone(color?: string | null): ComparisonTone | null {
  const normalized = normalizeText(color)
  if (!normalized) return null
  if (['green', 'success', 'positive', 'emerald', 'teal'].includes(normalized)) return 'positive'
  if (['yellow', 'warning', 'amber', 'orange'].includes(normalized)) return 'caution'
  if (['red', 'danger', 'negative'].includes(normalized)) return 'negative'
  return null
}

export function getComparisonPreference(label?: string): ComparisonPreference {
  const normalized = normalizeText(label)
  if (!normalized) return 'neutral'

  if (LOWER_BETTER_KEYWORDS.some((keyword) => normalized.includes(keyword))) {
    return 'lower_better'
  }
  if (HIGHER_BETTER_KEYWORDS.some((keyword) => normalized.includes(keyword))) {
    return 'higher_better'
  }
  return 'neutral'
}

export function getComparisonPreferenceLabel(label?: string): string | null {
  const preference = getComparisonPreference(label)
  if (preference === 'higher_better') return 'Higher is better'
  if (preference === 'lower_better') return 'Lower is better'
  return null
}

export function getAxisDirectionHint(label: string | undefined, axis: 'x' | 'y'): string | null {
  const preference = getComparisonPreference(label)
  if (preference === 'neutral') return null

  if (axis === 'x') {
    return preference === 'higher_better' ? 'More favorable to the right' : 'More favorable to the left'
  }

  return preference === 'higher_better' ? 'More favorable toward the top' : 'More favorable toward the bottom'
}

export function getFavorableQuadrantKey(
  xLabel?: string,
  yLabel?: string
): 'top_left' | 'top_right' | 'bottom_left' | 'bottom_right' {
  const xPreference = getComparisonPreference(xLabel)
  const yPreference = getComparisonPreference(yLabel)

  const horizontal = xPreference === 'lower_better' ? 'left' : 'right'
  const vertical = yPreference === 'lower_better' ? 'bottom' : 'top'

  return `${vertical}_${horizontal}` as 'top_left' | 'top_right' | 'bottom_left' | 'bottom_right'
}

function toneFromScaleWord(text: string, preference: ComparisonPreference): ComparisonTone | null {
  if (preference === 'neutral') return null

  const hasHigh = /\bhigh\b|\bstrong\b|\bleading\b|\bleader\b|\bexcellent\b/.test(text)
  const hasMedium = /\bmedium\b|\bmoderate\b|\bpartial\b|\bmixed\b/.test(text)
  const hasLow = /\blow\b|\bweak\b|\bpoor\b|\bminimal\b|\bnone\b/.test(text)

  if (hasMedium) return 'caution'
  if (!hasHigh && !hasLow) return null

  if (preference === 'higher_better') {
    if (hasHigh) return 'positive'
    if (hasLow) return 'negative'
  }

  if (preference === 'lower_better') {
    if (hasLow) return 'positive'
    if (hasHigh) return 'negative'
  }

  return null
}

function toneFromText(display: string, rowLabel?: string): ComparisonTone | null {
  const normalized = normalizeText(display)
  if (!normalized || normalized === '—') return 'neutral'

  const preference = getComparisonPreference(rowLabel)
  const scaleTone = toneFromScaleWord(normalized, preference)
  if (scaleTone) return scaleTone

  if (/\bno\b|\bfalse\b/.test(normalized)) return 'negative'
  if (/\byes\b|\btrue\b/.test(normalized)) return 'positive'

  if (NEGATIVE_TERMS.some((term) => normalized.includes(term))) return 'negative'
  if (CAUTION_TERMS.some((term) => normalized.includes(term))) return 'caution'
  if (POSITIVE_TERMS.some((term) => normalized.includes(term))) return 'positive'

  return null
}

function tonesFromNumericRow(
  value: unknown,
  rowValues: unknown[] = [],
  rowLabel?: string
): ComparisonTone | null {
  const preference = getComparisonPreference(rowLabel)
  if (preference === 'neutral') return null

  const current = parseComparableNumber(value)
  const numericValues = rowValues
    .map((entry) => parseComparableNumber(entry))
    .filter((entry): entry is number => entry !== null)

  if (current === null || numericValues.length < 2) return null

  const best = preference === 'higher_better' ? Math.max(...numericValues) : Math.min(...numericValues)
  const worst = preference === 'higher_better' ? Math.min(...numericValues) : Math.max(...numericValues)

  if (Math.abs(best - worst) < 1e-9) return 'neutral'
  if (Math.abs(current - best) < 1e-9) return 'positive'
  if (Math.abs(current - worst) < 1e-9) return 'negative'
  return 'caution'
}

export function getComparisonToneStyles(tone: ComparisonTone): {
  badge: string
  cell: string
  text: string
  dot: string
} {
  switch (tone) {
    case 'positive':
      return {
        badge: 'border border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-200',
        cell: 'bg-emerald-100/80 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200',
        text: 'text-emerald-800 dark:text-emerald-200',
        dot: 'bg-emerald-500',
      }
    case 'caution':
      return {
        badge: 'border border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-700 dark:bg-amber-950/60 dark:text-amber-200',
        cell: 'bg-amber-100/80 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
        text: 'text-amber-800 dark:text-amber-200',
        dot: 'bg-amber-500',
      }
    case 'negative':
      return {
        badge: 'border border-rose-300 bg-rose-100 text-rose-800 dark:border-rose-700 dark:bg-rose-950/60 dark:text-rose-200',
        cell: 'bg-rose-100/80 text-rose-900 dark:bg-rose-950/40 dark:text-rose-200',
        text: 'text-rose-800 dark:text-rose-200',
        dot: 'bg-rose-500',
      }
    default:
      return {
        badge: 'border border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-300',
        cell: 'text-gray-700 dark:text-gray-300',
        text: 'text-gray-600 dark:text-gray-400',
        dot: 'bg-gray-400',
      }
  }
}

export function getComparisonPresentation(args: ComparisonPresentationArgs) {
  const display = getComparisonDisplayValue(args.value)
  const explicitColor =
    args.explicitColor ??
    (typeof args.value === 'object' && args.value !== null ? String((args.value as Record<string, unknown>).color || '') : '')

  const tone =
    mapExplicitColorToTone(explicitColor) ??
    tonesFromNumericRow(args.value, args.rowValues, args.rowLabel) ??
    toneFromText(display, args.rowLabel) ??
    'neutral'

  return {
    display,
    tone,
    styles: getComparisonToneStyles(tone),
    preferenceLabel: getComparisonPreferenceLabel(args.rowLabel),
  }
}

export function ComparisonLegend({ note }: { note?: string }) {
  const items: Array<{ tone: ComparisonTone; label: string }> = [
    { tone: 'positive', label: 'More favorable' },
    { tone: 'caution', label: 'Middle / trade-off' },
    { tone: 'negative', label: 'Less favorable' },
  ]

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
      {items.map((item) => {
        const styles = getComparisonToneStyles(item.tone)
        return (
          <span
            key={item.label}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 font-medium shadow-sm ${styles.badge}`}
          >
            <span className={`h-2 w-2 rounded-full ${styles.dot}`} />
            {item.label}
          </span>
        )
      })}
      <span className="text-gray-400 dark:text-gray-500">
        {note || 'Color shows favorability within each row.'}
      </span>
    </div>
  )
}
